import classNames from "classnames";
import find from "lodash/find";
import map from "lodash/map";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { fontTagToCssClass } from "../../../../agreed/fonts";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { EditedText, Panel } from "../../../../states/customization";
import {
  findEditableTextDefinition,
  findFontDefinition,
  findPageAndDefinition,
} from "../../../../utils/customization/finders";
import EditorsValidationHeader from "../../../molecules/customization/EditorsValidationHeader";
import Page from "../../../molecules/customization/Page";
import "./style.scss";

interface Props {
  editedText: EditedText;
}

const pageBarHeight = 110 + 64 + 16; // bottom bar + EditorsValidationHeader heights + margin

const TextEditor: FunctionComponent<Props> = ({ editedText }) => {
  const {
    dispatch,
    content,
    template,
    ui: { defaultFontKey, panel },
    undoRedo,
  } = useCustomizationContext();
  const { pageKey, elementDefinitionKey, undoKey } = editedText;
  const [initialHistoryCount] = useState<number>(undoRedo.undoStack.length);

  const handleClose = () => {
    dispatch({ type: "setPageToFocus", pageKey });
    dispatch({
      key: pageKey,
      openedTab: panel.openedTab,
      panel: pageDefinition.type === "front-cover" ? Panel.EDITCOVER : Panel.EDITPAGE,
      type: "setPanel",
    });
    dispatch({
      editedText: null,
      type: "setEditedText",
    });
  };

  const cancelAndExit = useCallback(() => {
    if (
      initialHistoryCount < undoRedo.undoStack.length &&
      (!undoKey || undoKey === undoRedo.lastUndoKey)
    ) {
      dispatch({ type: "undoRedo", way: "undo" });
    }
    handleClose();
  }, [dispatch, initialHistoryCount, undoRedo.undoStack.length]);

  useEffect(() => {
    function escToClose(event: KeyboardEvent) {
      if (event.key === "Escape") {
        cancelAndExit();
      }
    }

    window.addEventListener("keydown", escToClose);

    return function cleanup() {
      window.removeEventListener("keydown", escToClose);
    };
  }, [cancelAndExit]);

  const { page, pageDefinition } = findPageAndDefinition(pageKey, template, content);
  const elementDefinition = findEditableTextDefinition(elementDefinitionKey, pageDefinition);
  const textOrientation = elementDefinition.textOrientation;
  const pagePpmmHeightRatio = (window.innerHeight - pageBarHeight) / pageDefinition.height.value;
  const pagePpmmWidthRatio = (window.innerWidth - pageBarHeight) / pageDefinition.width.value;
  const pagePpmmRatio = Math.min(pagePpmmHeightRatio, pagePpmmWidthRatio);

  const element = find(
    page.editableTexts,
    ({ editableTextKey }) => editableTextKey === elementDefinition.key
  );

  const defaultFontDefinition = defaultFontKey
    ? findFontDefinition(defaultFontKey, template)
    : undefined;
  const currentFontDefinition = element
    ? findFontDefinition(element.fontKey, template)
    : defaultFontDefinition || template.fontDefinitions[0];

  let textEditorBar = null;
  if (template.fontDefinitions.length > 1) {
    textEditorBar = (
      <div className="TextEditor-bar">
        {map(template.fontDefinitions, (fontDefinition) => {
          const onClick = () =>
            dispatch({
              editableTextKey: elementDefinitionKey,
              fontKey: fontDefinition.key,
              pageKey,
              type: "changeEditableTextElementFont",
              undoKey: editedText.undoKey,
            });

          return (
            <div
              key={fontDefinition.fontTag}
              className={classNames(
                "TextEditor-bar-font",
                fontTagToCssClass(fontDefinition.fontTag),
                {
                  "TextEditor-bar-font--selected":
                    fontDefinition.fontTag === currentFontDefinition.fontTag,
                }
              )}
              onClick={onClick}
            >
              Abc
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="TextEditor">
      <div className="TextEditor-overlay" />
      <div className="TextEditor-wrapper">
        <EditorsValidationHeader
          onCancel={cancelAndExit}
          onValidate={handleClose}
          pageKey={pageKey}
        />

        <div className="TextEditor-main">
          <div
            className="TextEditor-main-page"
            style={{ transform: `rotate(${textOrientation}deg)` }}
          >
            <Page content={content} pageKey={pageKey} template={template} ppmm={pagePpmmRatio} />
          </div>
        </div>
        {textEditorBar}
      </div>
    </div>
  );
};

export default TextEditor;
